export function normalizeTimestampMs(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return null;
  return num < 1e11 ? num * 1000 : num;
}

export function normalizeSegment(raw) {
  const startMs = normalizeTimestampMs(raw?.startMs);
  const endMs = normalizeTimestampMs(raw?.endMs);
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) return null;
  if (endMs <= startMs) return null;
  const maxDurationMs = 24 * 60 * 60 * 1000;
  const safeEndMs = endMs - startMs > maxDurationMs ? startMs + maxDurationMs : endMs;
  return { ...raw, startMs, endMs: safeEndMs };
}

export function getDayString(timestampMs, serverTz) {
  const fmt = new Intl.DateTimeFormat(undefined, {
    timeZone: serverTz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  const parts = fmt.formatToParts(new Date(timestampMs));
  return `${parts.find(p => p.type === 'year')?.value}-${parts.find(p => p.type === 'month')?.value}-${parts.find(p => p.type === 'day')?.value}`;
}

function getAllSegments(timelineData) {
  return timelineData
    .flatMap(d => d?.segments || [])
    .map(normalizeSegment)
    .filter(Boolean)
    .sort((a, b) => a.startMs - b.startMs);
}

function findSegmentForTimestamp(allSegments, timestampMs) {
  return allSegments.find(seg => timestampMs >= seg.startMs && timestampMs <= seg.endMs);
}

export function snapToNearestSegment(timestampMs, timelineData, serverTz) {
  const normalized = normalizeTimestampMs(timestampMs);
  if (!Number.isFinite(normalized)) return timestampMs;

  const allSegments = getAllSegments(timelineData);
  if (findSegmentForTimestamp(allSegments, normalized)) return normalized;
  if (allSegments.length === 0) return normalized;

  const targetDayString = getDayString(normalized, serverTz);
  const sameDaySegments = allSegments.filter(seg => getDayString(seg.startMs, serverTz) === targetDayString);

  if (sameDaySegments.length > 0) {
    let closestEdge = null;
    let minDistance = Infinity;
    let isStart = false;

    for (const seg of sameDaySegments) {
      const distanceToStart = Math.abs(normalized - seg.startMs);
      if (distanceToStart < minDistance || (distanceToStart === minDistance && !isStart)) {
        minDistance = distanceToStart;
        closestEdge = seg.startMs;
        isStart = true;
      }

      const distanceToEnd = Math.abs(normalized - seg.endMs);
      if (distanceToEnd < minDistance) {
        minDistance = distanceToEnd;
        closestEdge = seg.endMs;
        isStart = false;
      }
    }

    return closestEdge !== null ? closestEdge : normalized;
  }

  let closestEdge = null;
  let minDistance = Infinity;

  for (const seg of allSegments) {
    const distanceToStart = Math.abs(normalized - seg.startMs);
    if (distanceToStart < minDistance) {
      minDistance = distanceToStart;
      closestEdge = seg.startMs;
    }

    const distanceToEnd = Math.abs(normalized - seg.endMs);
    if (distanceToEnd < minDistance) {
      minDistance = distanceToEnd;
      closestEdge = seg.endMs;
    }
  }

  return closestEdge !== null ? closestEdge : normalized;
}

export function calculateVideoPosition(timestampMs, timelineData, serverTz) {
  const normalizedTimestampMs = normalizeTimestampMs(timestampMs);
  if (!Number.isFinite(normalizedTimestampMs)) {
    console.warn('Invalid timestamp for seek:', timestampMs);
    return 0;
  }

  const allSegments = getAllSegments(timelineData);
  const segment = findSegmentForTimestamp(allSegments, normalizedTimestampMs);

  if (!segment) {
    console.warn('No segment found for timestamp:', new Date(normalizedTimestampMs).toISOString());
    return 0;
  }

  const targetDayString = getDayString(normalizedTimestampMs, serverTz);
  const dayData = timelineData.find(d => (d?.date || d?.day) === targetDayString);
  const sameDaySegments = (dayData?.segments || [])
    .map(normalizeSegment)
    .filter(Boolean)
    .sort((a, b) => a.startMs - b.startMs);

  if (sameDaySegments.length === 0) {
    console.warn('No segments found for day:', targetDayString);
    return 0;
  }

  const targetSegment = sameDaySegments.find(seg =>
    normalizedTimestampMs >= seg.startMs && normalizedTimestampMs <= seg.endMs
  );
  if (!targetSegment) return 0;

  let totalMs = 0;
  for (const seg of sameDaySegments) {
    if (seg.endMs <= targetSegment.startMs) {
      totalMs += (seg.endMs - seg.startMs);
    } else if (seg.startMs < targetSegment.startMs) {
      totalMs += Math.min(seg.endMs, targetSegment.startMs) - seg.startMs;
    } else {
      break;
    }
  }

  const offsetInSegment = normalizedTimestampMs - targetSegment.startMs;
  totalMs += Math.max(0, offsetInSegment);

  const dayDurationMs = sameDaySegments.reduce((sum, seg) => sum + (seg.endMs - seg.startMs), 0);
  const videoPositionSec = Math.min(dayDurationMs / 1000, Math.max(0, totalMs / 1000));
  if (videoPositionSec > 86400) {
    console.warn('Clamping oversized seek position:', videoPositionSec, 'for day', targetDayString);
  }

  return Math.min(videoPositionSec, 86400);
}
